/**
 * PDFGenerationService
 * Generates PDF documents for contracts with user data
 */

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { PDFFont } from 'pdf-lib';
import { createLogger, getErrorMessage } from '../logger';

const logger = createLogger('PDFGenerationService');

/**
 * Strip HTML tags from text for PDF rendering
 */
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .trim();
}

export interface ContractPDFData {
  contractName: string;
  contractDescription: string;
  contractTerms: string;
  companyName: string;
  purpose: string;
  submittedData: Record<string, unknown>;
  fieldLabels: Record<string, string>;
}

function getSubmittedFields(
  submittedData: Record<string, unknown>,
): Record<string, unknown> {
  if ('fields' in submittedData) {
    const fields = submittedData.fields;
    if (typeof fields === 'object' && fields !== null) {
      logger.warn('Detected nested data structure, extracting fields');
      return fields as Record<string, unknown>;
    }
  }

  return submittedData;
}

export class PDFGenerationService {
  /**
   * Generates a PDF document for a contract with user data
   */
  static async generateContractPDF(data: ContractPDFData): Promise<Blob> {
    try {
      const pdfDoc = await PDFDocument.create();
      const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const timesRomanBold = await pdfDoc.embedFont(
        StandardFonts.TimesRomanBold,
      );

      let page = pdfDoc.addPage();
      const { width, height } = page.getSize();
      const fontSize = 12;
      const titleFontSize = 18;
      const headingFontSize = 14;
      const margin = 50;
      let yPosition = height - margin;

      // Helper function to add new page if needed
      const checkAndAddPage = (requiredSpace: number) => {
        if (yPosition - requiredSpace < margin) {
          page = pdfDoc.addPage();
          yPosition = height - margin;
        }
      };

      // Helper function to draw wrapped text
      const drawWrappedText = (
        text: string,
        font: PDFFont,
        size: number,
        maxWidth: number,
      ) => {
        // Ensure text is a string and replace newlines with spaces
        const textStr = String(text || '')
          .replace(/[\n\r]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        const words = textStr.split(' ');
        let line = '';
        const lines: string[] = [];

        words.forEach((word) => {
          const testLine = line + word + ' ';
          const testWidth = font.widthOfTextAtSize(testLine, size);

          if (testWidth > maxWidth && line !== '') {
            lines.push(line.trim());
            line = word + ' ';
          } else {
            line = testLine;
          }
        });

        if (line.trim() !== '') {
          lines.push(line.trim());
        }

        lines.forEach((l) => {
          checkAndAddPage(size + 5);
          page.drawText(l, {
            x: margin,
            y: yPosition,
            size,
            font,
            color: rgb(0, 0, 0),
          });
          yPosition -= size + 5;
        });
      };

      // Title
      page.drawText(data.contractName, {
        x: margin,
        y: yPosition,
        size: titleFontSize,
        font: timesRomanBold,
        color: rgb(0, 0, 0),
      });
      yPosition -= titleFontSize + 20;

      // Company Name
      checkAndAddPage(fontSize + 10);
      page.drawText(`Company: ${data.companyName}`, {
        x: margin,
        y: yPosition,
        size: fontSize,
        font: timesRomanFont,
        color: rgb(0, 0, 0),
      });
      yPosition -= fontSize + 10;

      // Purpose
      checkAndAddPage(fontSize + 10);
      page.drawText(`Purpose: ${data.purpose}`, {
        x: margin,
        y: yPosition,
        size: fontSize,
        font: timesRomanFont,
        color: rgb(0, 0, 0),
      });
      yPosition -= fontSize + 20;

      // Description
      if (data.contractDescription) {
        checkAndAddPage(headingFontSize + 10);
        page.drawText('Description:', {
          x: margin,
          y: yPosition,
          size: headingFontSize,
          font: timesRomanBold,
          color: rgb(0, 0, 0),
        });
        yPosition -= headingFontSize + 10;

        drawWrappedText(
          data.contractDescription,
          timesRomanFont,
          fontSize,
          width - 2 * margin,
        );
        yPosition -= 10;
      }

      // Terms & Conditions
      if (data.contractTerms) {
        checkAndAddPage(headingFontSize + 10);
        page.drawText('Terms & Conditions:', {
          x: margin,
          y: yPosition,
          size: headingFontSize,
          font: timesRomanBold,
          color: rgb(0, 0, 0),
        });
        yPosition -= headingFontSize + 10;

        // Strip HTML tags from terms before rendering
        const cleanTerms = stripHtml(data.contractTerms);
        drawWrappedText(
          cleanTerms,
          timesRomanFont,
          fontSize - 1,
          width - 2 * margin,
        );
        yPosition -= 10;
      }

      // Submitted Data
      if (data.submittedData && Object.keys(data.submittedData).length > 0) {
        checkAndAddPage(headingFontSize + 10);
        page.drawText('Submitted Information:', {
          x: margin,
          y: yPosition,
          size: headingFontSize,
          font: timesRomanBold,
          color: rgb(0, 0, 0),
        });
        yPosition -= headingFontSize + 15;

        // Check if submittedData has the wrong nested structure (fields/metadata)
        const actualData = getSubmittedFields(data.submittedData);

        // Add submitted data fields
        Object.entries(actualData).forEach(([fieldId, value]) => {
          const label = data.fieldLabels[fieldId] || fieldId;
          checkAndAddPage(fontSize + 20);

          // Field label
          page.drawText(`${label}:`, {
            x: margin,
            y: yPosition,
            size: fontSize,
            font: timesRomanBold,
            color: rgb(0, 0, 0),
          });
          yPosition -= fontSize + 5;

          // Field value - ensure it's a string and handle objects
          let fieldValue = '(Not provided)';
          if (value !== null && value !== undefined) {
            if (typeof value === 'object') {
              // If it's an object, try to extract a meaningful value
              fieldValue = JSON.stringify(value, null, 2).replace(
                /[\n\r]+/g,
                ' ',
              );
            } else {
              fieldValue = String(value);
            }
          }
          drawWrappedText(
            fieldValue,
            timesRomanFont,
            fontSize,
            width - 2 * margin,
          );
          yPosition -= 10;
        });
      }

      // Signature section
      checkAndAddPage(100);
      yPosition -= 20;

      page.drawText('Signature:', {
        x: margin,
        y: yPosition,
        size: headingFontSize,
        font: timesRomanBold,
        color: rgb(0, 0, 0),
      });
      yPosition -= headingFontSize + 20;

      // Signature line
      page.drawLine({
        start: { x: margin, y: yPosition },
        end: { x: width - margin, y: yPosition },
        thickness: 1,
        color: rgb(0, 0, 0),
      });
      yPosition -= 20;

      // Date line
      page.drawText('Date: ___________________', {
        x: margin,
        y: yPosition,
        size: fontSize,
        font: timesRomanFont,
        color: rgb(0, 0, 0),
      });

      // Serialize PDF
      const pdfBytes = await pdfDoc.save();
      return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
    } catch (error) {
      logger.error('PDF generation error:', {
        error: getErrorMessage(error, 'PDF generation error'),
        data: {
          contractName: data.contractName,
          submittedDataKeys: Object.keys(data.submittedData),
          fieldLabelsKeys: Object.keys(data.fieldLabels || {}),
        },
      });
      throw new Error(
        `Failed to generate PDF document: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Downloads a PDF blob with a given filename
   */
  static downloadPDF(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Generates and downloads a contract PDF
   */
  static async generateAndDownloadContract(
    data: ContractPDFData,
  ): Promise<void> {
    const pdfBlob = await this.generateContractPDF(data);
    const filename = `${data.contractName.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`;
    this.downloadPDF(pdfBlob, filename);
  }
}
